import { useMemo, type ReactElement, type ReactNode } from 'react';
import { MetadataProvider, fragmentsFromArtifacts, type FragmentLoader, type MetadataArtifact } from '@zagru/metadata-renderer';
import { toListQuery, useApiClient, type ApiClient } from '@zagru/platform';
import { artifactSchema, artifactVersionSchema, type Artifact } from '@/api/platform';
import { z } from 'zod';

const publishedScreensPage = z.object({ content: z.array(artifactSchema) });

/**
 * Loads every published SCREEN artifact once (content of the published version) and serves fragments per slot id.
 * Screens carry `content.slot`; `fragmentsFromArtifacts` groups them by slot.
 */
export function createArtifactFragmentLoader(client: ApiClient): FragmentLoader {
  let cache: Promise<Record<string, ReturnType<typeof fragmentsFromArtifacts>[string]>> | null = null;

  const loadAll = async () => {
    const page = await client.get('/api/v1/platform/metadata/artifacts', {
      query: toListQuery({ page: 0, size: 200, filter: { kind: 'SCREEN' } }),
      schema: publishedScreensPage,
    });
    const published = page.content.filter((a: Artifact) => a.currentPublishedVersion != null);
    const artifacts: MetadataArtifact[] = await Promise.all(
      published.map(async (a) => {
        const version = await client.get(`/api/v1/platform/metadata/artifacts/${a.id}/versions/${a.currentPublishedVersion}`, {
          schema: artifactVersionSchema,
        });
        return { id: a.id, key: a.key, kind: a.kind, status: 'PUBLISHED', version: version.version, content: version.content };
      }),
    );
    return fragmentsFromArtifacts(artifacts);
  };

  return async (slotId) => {
    if (!cache) {
      cache = loadAll().catch((err: unknown) => {
        cache = null;
        throw err;
      });
    }
    const bySlot = await cache;
    return bySlot[slotId] ?? [];
  };
}

/** Mounts the metadata renderer provider with the artifact-backed fragment loader. */
export function MetadataFragmentsProvider({ children }: { children: ReactNode }): ReactElement {
  const client = useApiClient();
  const loadFragments = useMemo(() => createArtifactFragmentLoader(client), [client]);
  return <MetadataProvider loadFragments={loadFragments}>{children}</MetadataProvider>;
}
